import React, { useState, useEffect } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, doc, setDoc } from 'firebase/firestore';
import {
  auth, db, googleProvider, facebookProvider, twitterProvider, githubProvider,
} from '../config/firebase';
import emailjs from '@emailjs/browser';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaGoogle, FaFacebook, FaTwitter, FaGithub,
  FaSignOutAlt, FaPaperPlane, FaLock, FaUnlock, FaCheckCircle, FaSpinner,
} from 'react-icons/fa';
import { usePageData } from '../hooks/useFirestoreData';
import '../styles/Contact.css';

const DEFAULT_CONTACT = {
  subtitle: 'Have a project in mind or just want to connect? Send me a message below.',
  successMessage: "Thanks for reaching out. I'll get back to you soon.",
};

const AUTH_PROVIDERS = [
  { key: 'google', label: 'Google', icon: <FaGoogle />, provider: googleProvider, className: 'auth-google' },
  { key: 'facebook', label: 'Facebook', icon: <FaFacebook />, provider: facebookProvider, className: 'auth-facebook' },
  { key: 'twitter', label: 'Twitter (X)', icon: <FaTwitter />, provider: twitterProvider, className: 'auth-twitter' },
  { key: 'github', label: 'GitHub', icon: <FaGithub />, provider: githubProvider, className: 'auth-github' },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.8, 0.25, 1] },
  }),
};

/** Generates a Firestore doc ID from timestamp + email */
function getDateKey(dateObj) {
  const pad = (n) => String(n).padStart(2, '0');
  const d = dateObj;
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}:${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

function Contact() {
  const { data } = usePageData('contact', DEFAULT_CONTACT);
  const [user, setUser] = useState(null);
  const [providerName, setProviderName] = useState('');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [authError, setAuthError] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const pd = firebaseUser.providerData?.[0];
        setUser(firebaseUser);
        setProviderName(pd?.providerId || '');
        setForm(prev => ({
          ...prev,
          name: pd?.displayName || firebaseUser.displayName || '',
          email: firebaseUser.email || pd?.email || '',
        }));
      } else {
        setUser(null);
        setProviderName('');
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSignIn = async (providerObj) => {
    try {
      setAuthError('');
      await signInWithPopup(auth, providerObj);
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') return;
      if (error.code === 'auth/account-exists-with-different-credential') {
        setAuthError('An account already exists with the same email. Try a different provider.');
      } else {
        setAuthError(`Sign-in failed: ${error.message}`);
      }
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setForm({ name: '', email: '', subject: '', message: '' });
    setSubmitted(false);
    setSubmitError('');
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSubmitError('');

    const now = new Date();
    const docId = `${getDateKey(now)}-${form.email}`;

    try {
      // 1. Save to Firestore directly (no cloud function needed)
      await setDoc(doc(collection(db, 'messages'), docId), {
        sentOn: now,
        subject: form.subject || '(No subject)',
        message: form.message,
        email: form.email,
        authorisedBy: providerName,
        name: form.name,
        photo: user?.photoURL || '',
      });

      // 2. Send email notification via EmailJS (free: 200/month)
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (serviceId && templateId && publicKey &&
          serviceId !== 'your_service_id') {
        await emailjs.send(serviceId, templateId, {
          from_name: form.name,
          from_email: form.email,
          sender_photo: user?.photoURL || '',
          subject: form.subject || '(No subject)',
          message: form.message,
          provider: providerName.replace('.com', ''),
        }, publicKey);
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const isAuthenticated = !!user;

  return (
    <section className="contact page-wrapper">
      <div className="contact-container">
        <motion.h1
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Contact
        </motion.h1>

        <motion.p
          className="contact-subtitle"
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeInUp} custom={1}
        >
          {data.subtitle}
        </motion.p>

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              className="success-card glass-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <FaCheckCircle className="success-icon" />
              <h2>Message Sent!</h2>
              <p>{data.successMessage}</p>
              <button className="btn btn-outline" onClick={() => {
                setSubmitted(false);
                setForm(prev => ({ ...prev, subject: '', message: '' }));
              }}>
                Send Another
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              className="contact-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Auth panel */}
              <motion.div
                className="glass-card auth-panel"
                initial="hidden" whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp} custom={1}
              >
                <div className="auth-header">
                  {isAuthenticated ? <FaUnlock className="auth-lock" /> : <FaLock className="auth-lock" />}
                  <h3>{isAuthenticated ? 'Authenticated' : 'Sign In to Send'}</h3>
                </div>

                {!isAuthenticated ? (
                  <div className="auth-buttons">
                    <p className="auth-desc">
                      Verify your identity to enable the contact form.
                    </p>
                    {AUTH_PROVIDERS.map(({ key, label, icon, provider, className }) => (
                      <button
                        key={key}
                        className={`auth-btn ${className}`}
                        onClick={() => handleSignIn(provider)}
                      >
                        {icon} {label}
                      </button>
                    ))}
                    {authError && <p className="auth-error">{authError}</p>}
                  </div>
                ) : (
                  <div className="auth-user-info">
                    <div className="auth-avatar">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt={user.displayName} />
                      ) : (
                        <span className="auth-avatar-initials">
                          {(user.displayName || 'U')?.split(' ').map(n => n[0]).join('')}
                        </span>
                      )}
                    </div>
                    <p className="auth-name">{user.displayName}</p>
                    <p className="auth-email">{user.email}</p>
                    {providerName && (
                      <span className="auth-provider">via {providerName.replace('.com', '')}</span>
                    )}
                    <button className="btn-signout" onClick={handleSignOut}>
                      <FaSignOutAlt /> Sign Out
                    </button>
                  </div>
                )}
              </motion.div>

              {/* Contact form */}
              <motion.form
                className="glass-card contact-form"
                onSubmit={handleSubmit}
                initial="hidden" whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp} custom={2}
              >
                <div className="form-group">
                  <label htmlFor="name">
                    <span className="label-prefix">&gt; </span>Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    disabled={!isAuthenticated}
                    readOnly={isAuthenticated && !!form.name}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    <span className="label-prefix">&gt; </span>Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder={isAuthenticated && !form.email ? 'Please enter your email' : 'your@email.com'}
                    disabled={!isAuthenticated}
                    readOnly={isAuthenticated && !!form.email}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">
                    <span className="label-prefix">&gt; </span>Subject
                    <span className="char-count">{form.subject.length}/70</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    required
                    maxLength={70}
                    placeholder={isAuthenticated ? 'What is this about?' : 'Sign in to unlock'}
                    disabled={!isAuthenticated}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">
                    <span className="label-prefix">&gt; </span>Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={form.message}
                    onChange={handleChange}
                    required
                    placeholder={isAuthenticated ? 'Type your message...' : 'Sign in to unlock'}
                    disabled={!isAuthenticated}
                  />
                </div>

                {submitError && <p className="submit-error">{submitError}</p>}

                <button
                  type="submit"
                  className={`btn btn-primary submit-btn ${!isAuthenticated || sending ? 'btn-disabled' : ''}`}
                  disabled={!isAuthenticated || sending}
                >
                  {sending ? (
                    <><FaSpinner className="spin-icon" /> Sending...</>
                  ) : isAuthenticated ? (
                    <><FaPaperPlane /> Send Message</>
                  ) : (
                    <><FaLock /> Sign In Required</>
                  )}
                </button>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

export default Contact;
